import { Button, ButtonGroup, Popup, Label, Input, Checkbox, Icon, List } from "semantic-ui-react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { copyObject } from "../../redux/modules/aws-bucket";
import { timeAgo, defaultPrefix, getFilename, prefix, lockPrefix } from '../../utils/pay-utils';
import { useRefreshJob, REFRESH_STATUS } from '../RefreshJobProvider';


function ReportStatus({ dispatch, billingData, startDate, onRefresh, user, reportParams, reportType }) {
    const current = `pay-data/${moment(startDate).format('MM-YYYY')}.gz`;
    const [isLocked, setIsLocked] = useState(false);
    const [loading, setLoading] = useState(false);
    const refreshJob = useRefreshJob();
    const isRefreshing = refreshJob && (refreshJob.status === REFRESH_STATUS.CONNECTING || refreshJob.status === REFRESH_STATUS.RUNNING);

    const handleAsyncRefresh = () => {
        if (!refreshJob || isRefreshing) return;
        // Connects, subscribes, then triggers the job over the same socket; the result is shown in a modal
        refreshJob.startRefresh(reportParams, reportType);
    };
    const [currentTime, setCurrentTime] = useState(new Date());
    const [openLockPopup, setOpenLockPopup] = useState(false);
    const [openFilesPopup, setOpenFilesPopup] = useState(false);
    const [isDefault, setIsDefault] = useState(false);
    const [fileName, setFileName] = useState('');

    useEffect(() => {
        const intervalId = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);

        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        setIsLocked(billingData?.filename?.startsWith(lockPrefix) ?? false);
    }, [billingData, startDate]);

    const lastUpdated = useMemo(() => {
        const file = billingData?.metadata?.Contents?.find(f => f.Key === billingData?.filename);
        return moment(file?.LastModified);
    }, [billingData?.metadata, startDate, currentTime]);

    const isLatest = useMemo(() => moment().diff(lastUpdated, 'hours') < 2, [lastUpdated])

    const handleLock = async (forceDefault) => {
        setLoading(true);
        setOpenLockPopup(false);
        const name = fileName?.length ? fileName : moment().milliseconds();
        const date = moment(startDate).format('MM-YYYY');
        const saveDefault = forceDefault === undefined ? isDefault : forceDefault;
        const key = saveDefault ? `${defaultPrefix}/${date}.gz` : `${lockPrefix}/${date}-${name}.gz`;
        await dispatch(copyObject({ key, source: current }));
        setIsLocked(true);
        setLoading(false);
        onRefresh(key);
    }

    const lockedFiles = useMemo(
        () => billingData?.metadata?.Contents
            .filter(f => f.Key?.startsWith(lockPrefix) && f.Key?.includes(moment(startDate).format('MM-YYYY')))
            .map(f => f.Key),
        [billingData?.metadata, startDate]
    );

    const toggleLock = (file) => {
        const toggleIsLocked = !isLocked;
        const date = moment(startDate).format('MM-YYYY');
        setIsLocked(toggleIsLocked);
        onRefresh(toggleIsLocked ? file : `${prefix}/${date}.gz`);
    }

    const RenderFileSelectionPopup = () => {
        return <Popup
            trigger={<Button loading={loading} content="Show Locked" icon="list" color="orange" />}
            content={
                <div>
                    <List divided={true}>
                        {lockedFiles.map((file, index) => (
                            <List.Item
                                key={index}
                                className="pl-3 pr-3 pt-3 pb-3 cursorPointer"
                                onClick={() => toggleLock(file)}
                            >
                                <List.Content>
                                    <Icon
                                        color={file.startsWith(defaultPrefix) ? 'orange' : 'grey'}
                                        name="lock"
                                    />
                                    {getFilename(file)}
                                </List.Content>
                            </List.Item>
                        ))}
                    </List>
                </div>
            }
            on='click'
            open={openFilesPopup}
            onClose={() => setOpenFilesPopup(false)}
            onOpen={() => setOpenFilesPopup(true)}
            position='bottom right'
        />;
    }

    return <div className="float-right">
        {user.isAdmin && <Label content={isLocked ? getFilename(billingData?.filename ?? startDate) : 'Latest Report'}
            color={isLocked ? 'red' : isLatest ? 'green' : 'orange'}
            icon={isLocked ? 'lock' : isLatest ? 'check' : 'warning'}
            basic={true}
            className="mr-2"
            title={lastUpdated.format('MMMM Do YYYY, h:mm:ss a')}
        />}

        {billingData?.filename && <>Last updated: <b>{timeAgo(lastUpdated)}</b> ago</>}

        {user.isAdmin && user.isPayReportAdmin && <ButtonGroup className="ml-2">
            {!isLocked && user.isPayReportAdmin &&
                <Button
                    icon={isRefreshing ? 'spinner' : 'refresh'}
                    loading={isRefreshing}
                    color="blue"
                    basic={true}
                    disabled={isRefreshing}
                    title={isRefreshing ? 'Refresh running in background…' : 'Refresh report data'}
                    onClick={handleAsyncRefresh}
                />}
            {!isLocked && user.isPayReportAdmin && <Popup
                trigger={<Button loading={loading} content="Lock Report" icon="lock" color="red" />}
                content={
                    <div className="p-4">
                        <p>
                            Are you sure you want to lock this report?<br />
                        </p>

                        {
                            <Checkbox
                                checked={isDefault}
                                onChange={(e, { checked }) => setIsDefault(checked)}
                                label="Set this as default" toggle
                            />
                        }
                        {!isDefault &&
                            <>
                                <br />
                                <br />
                                <Input
                                    placeholder='Report name'
                                    value={fileName}
                                    onChange={(e, { value }) => setFileName(value)}
                                />
                            </>

                        }
                        <br />
                        <br />
                        {!!lockedFiles?.length && isDefault &&
                            <p className="text-red"><Icon name="exclamation" />
                                Overwrite the default {moment(startDate).format('MM/YYYY')} report?
                            </p>
                        }
                        <Button
                            size="small"
                            loading={loading}
                            disabled={!(isDefault || fileName?.length > 2)}
                            onClick={() => handleLock()}
                            content="Yes, Lock Report"
                            icon="lock" color="red"
                        />
                    </div>
                }
                on='click'
                open={openLockPopup}
                onClose={() => setOpenLockPopup(false)}
                onOpen={() => setOpenLockPopup(true)}
                position='bottom right'
            />}
            {lockedFiles?.length && user.isPayReportAdmin && isLocked && !billingData?.filename?.startsWith(defaultPrefix) &&
                <Button basic={true} color="red" onClick={() => handleLock(true)} content="Set as Default" />}
            {lockedFiles?.length && isLocked && <Button color="green" content="Show Latest" onClick={toggleLock} />}
            {lockedFiles?.length && <RenderFileSelectionPopup />}
        </ButtonGroup>}
    </div>
}

export default ReportStatus;